/**
 * A small map with one pin — for "show me where" on a form, and for reading
 * back where something was reported.
 *
 * Deliberately not Leaflet. The Government Portal's field and team maps use
 * react-leaflet, but that is an app dependency of `gov` alone, and a resident
 * placing one pin on a report form needs a few raster tiles, a click and a
 * marker — not a mapping library in a public bundle. This draws the slippy-map
 * tiles itself (Web Mercator, 256 px tiles) and converts a click back to a
 * coordinate.
 *
 * Accessible without a pointer: when editable, the map is focusable and the
 * arrow keys move the pin (about 10 m a press at street zoom), + and − zoom.
 */
import React, { useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { Box, IconButton, Stack, Typography } from '../primitives';
import * as Icons from '../icons';
import { iconSize } from '@munifocus/tokens';
import { usePortalPalette } from '../theme/usePortalPalette';

export interface PinMapPoint {
  latitude: number;
  longitude: number;
}

export interface PinMapMarker extends PinMapPoint {
  id: string;
  /** Read out and shown on hover. */
  label: string;
}

export interface PinMapProps {
  /** The pin. Null shows the map at `center` with no pin. */
  value: PinMapPoint | null;
  /** Where to look when there is no pin yet. */
  center: PinMapPoint;
  /** Present to make the map editable: a click or arrow key moves the pin. */
  onChange?: (value: PinMapPoint) => void;
  /** Other points, drawn smaller and read-only (e.g. likely duplicates). */
  markers?: PinMapMarker[];
  /** Accessible name for the map region. */
  label: string;
  /** Shown under the map; OpenStreetMap's licence requires it. */
  attribution?: string;
  zoomInLabel?: string;
  zoomOutLabel?: string;
  height?: number;
  initialZoom?: number;
  /** `{z}/{x}/{y}` raster tile template. Defaults to OpenStreetMap. */
  tileUrl?: string;
}

const TILE = 256;
const MIN_ZOOM = 12;
const MAX_ZOOM = 19;
const DEFAULT_TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

/** World pixel coordinates of a point at a zoom level (Web Mercator). */
export function pointToPixels(point: PinMapPoint, zoom: number): { x: number; y: number } {
  const scale = TILE * 2 ** zoom;
  const lat = Math.max(-85.0511, Math.min(85.0511, point.latitude));
  const sin = Math.sin((lat * Math.PI) / 180);
  return {
    x: ((point.longitude + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale,
  };
}

/** The inverse of {@link pointToPixels}. */
export function pixelsToPoint(pixel: { x: number; y: number }, zoom: number): PinMapPoint {
  const scale = TILE * 2 ** zoom;
  const longitude = (pixel.x / scale) * 360 - 180;
  const n = Math.PI - (2 * Math.PI * pixel.y) / scale;
  const latitude = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return { latitude, longitude };
}

/** Rounded to ~1 m, which is all a hand-placed pin can claim. */
function round(point: PinMapPoint): PinMapPoint {
  return {
    latitude: Math.round(point.latitude * 1e5) / 1e5,
    longitude: Math.round(point.longitude * 1e5) / 1e5,
  };
}

export function PinMap({
  value,
  center,
  onChange,
  markers = [],
  label,
  attribution = '© OpenStreetMap contributors',
  zoomInLabel = 'Zoom in',
  zoomOutLabel = 'Zoom out',
  height = 280,
  initialZoom = 17,
  tileUrl = DEFAULT_TILES,
}: PinMapProps) {
  const palette = usePortalPalette();
  const [zoom, setZoom] = useState(initialZoom);
  const ref = useRef<HTMLDivElement>(null);
  const editable = !!onChange;
  const focus = value ?? center;
  const origin = useMemo(() => pointToPixels(focus, zoom), [focus, zoom]);

  // Enough tiles to cover a wide form column: five across, three down.
  const tiles = useMemo(() => {
    const cx = Math.floor(origin.x / TILE);
    const cy = Math.floor(origin.y / TILE);
    const max = 2 ** zoom;
    const out: Array<{ key: string; src: string; left: number; top: number }> = [];
    for (let dx = -2; dx <= 2; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const tx = cx + dx;
        const ty = cy + dy;
        if (ty < 0 || ty >= max) continue;
        const wrapped = ((tx % max) + max) % max;
        out.push({
          key: `${zoom}/${tx}/${ty}`,
          src: tileUrl
            .replace('{z}', String(zoom))
            .replace('{x}', String(wrapped))
            .replace('{y}', String(ty)),
          left: tx * TILE - origin.x,
          top: ty * TILE - origin.y,
        });
      }
    }
    return out;
  }, [origin, zoom, tileUrl]);

  const place = (event: MouseEvent<HTMLDivElement>) => {
    if (!onChange || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const dx = event.clientX - rect.left - rect.width / 2;
    const dy = event.clientY - rect.top - rect.height / 2;
    onChange(round(pixelsToPoint({ x: origin.x + dx, y: origin.y + dy }, zoom)));
  };

  const onKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === '+' || event.key === '=') {
      setZoom((z) => Math.min(MAX_ZOOM, z + 1));
      event.preventDefault();
      return;
    }
    if (event.key === '-') {
      setZoom((z) => Math.max(MIN_ZOOM, z - 1));
      event.preventDefault();
      return;
    }
    if (!onChange) return;
    const step = 4; // pixels; ~10 m at zoom 17
    const move: Record<string, [number, number]> = {
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
    };
    const delta = move[event.key];
    if (!delta) return;
    event.preventDefault();
    onChange(round(pixelsToPoint({ x: origin.x + delta[0], y: origin.y + delta[1] }, zoom)));
  };

  const offset = (point: PinMapPoint) => {
    const p = pointToPixels(point, zoom);
    return { left: p.x - origin.x, top: p.y - origin.y };
  };

  return (
    <Stack spacing={0.5}>
      <Box
        ref={ref}
        aria-label={label}
        // Interactive only when editable: a focusable application region that
        // takes clicks and the arrow keys. Read-only, it is an image.
        {...(editable
          ? { role: 'application', tabIndex: 0, onClick: place, onKeyDown: onKey }
          : { role: 'img' })}
        sx={{
          position: 'relative',
          height,
          overflow: 'hidden',
          borderRadius: 1,
          border: `1px solid ${palette.border.line}`,
          backgroundColor: palette.background.parch,
          cursor: editable ? 'crosshair' : 'default',
          '&:focus-visible': { outline: `2px solid ${palette.border.focus}`, outlineOffset: 2 },
        }}
      >
        {tiles.map((tile) => (
          <Box
            key={tile.key}
            component="img"
            src={tile.src}
            alt=""
            draggable={false}
            sx={{
              position: 'absolute',
              width: TILE,
              height: TILE,
              left: `calc(50% + ${tile.left}px)`,
              top: `calc(50% + ${tile.top}px)`,
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          />
        ))}

        {markers.map((m) => {
          const at = offset(m);
          return (
            <Box
              key={m.id}
              title={m.label}
              aria-label={m.label}
              sx={{
                position: 'absolute',
                left: `calc(50% + ${at.left}px)`,
                top: `calc(50% + ${at.top}px)`,
                width: 12,
                height: 12,
                transform: 'translate(-50%, -50%)',
                borderRadius: '50%',
                backgroundColor: palette.semantic.pending,
                border: `2px solid ${palette.background.paper}`,
                pointerEvents: 'none',
              }}
            />
          );
        })}

        {value && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: `calc(50% + ${offset(value).left}px)`,
              top: `calc(50% + ${offset(value).top}px)`,
              transform: 'translate(-50%, -100%)',
              color: palette.brand.accent,
              pointerEvents: 'none',
              lineHeight: 0,
            }}
          >
            <Icons.MapPin size={iconSize.lg} fill={palette.background.paper} />
          </Box>
        )}

        <Stack spacing={0.5} sx={{ position: 'absolute', right: 8, top: 8 }}>
          {/* Zooming must not also move the pin, which a click on the map does. */}
          <IconButton
            size="small"
            aria-label={zoomInLabel}
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              setZoom((z) => Math.min(MAX_ZOOM, z + 1));
            }}
            sx={{ backgroundColor: palette.background.paper }}
          >
            <Icons.Plus size={iconSize.sm} />
          </IconButton>
          <IconButton
            size="small"
            aria-label={zoomOutLabel}
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              setZoom((z) => Math.max(MIN_ZOOM, z - 1));
            }}
            sx={{ backgroundColor: palette.background.paper }}
          >
            <Icons.Minus size={iconSize.sm} />
          </IconButton>
        </Stack>
      </Box>
      <Typography variant="caption" sx={{ color: palette.text.muted }}>
        {attribution}
      </Typography>
    </Stack>
  );
}
